import { useId, useState, useEffect } from 'react';

interface SVGGaugeProps {
    percent: number;
    value: string;
    label: string;
    fromColor: string;
    toColor: string;
    secondaryInfo?: string;
}

export default function SVGGauge({ percent, value, label, fromColor, toColor, secondaryInfo }: SVGGaugeProps) {
    const id = useId();
    const radius = 80;
    const circumference = Math.PI * radius;
    const safePercent = Math.min(1, Math.max(0, percent));
    const strokeDashoffset = circumference * (1 - safePercent);

    const [animatedPercent, setAnimatedPercent] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedPercent(safePercent);
        }, 50);
        return () => clearTimeout(timer);
    }, [safePercent]);

    const gradientId = `gauge-gradient-${id}`;
    const maskId = `gauge-mask-${id}`;

    return (
        <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 200 110" className="w-full h-auto overflow-visible">
                <defs>
                    {/*
            1. Definiujemy stały gradient rozpięty na całą szerokość (x1=20 do x2=180).
            On się nigdy nie przesuwa.
          */}
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={fromColor} />
                        <stop offset="80%" stopColor={fromColor} />
                        <stop offset="100%" stopColor={toColor} />
                    </linearGradient>

                    {/*
            2. Maska, która będzie sterować tym, ile gradientu widzimy.
          */}
                    <mask id={maskId}>
                        <path
                            d="M 20 100 A 80 80 0 0 1 180 100"
                            fill="none"
                            stroke="white"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                        />
                    </mask>
                </defs>

                {/* TŁO (Cień łuku) - pokazuje "widmo" całego gradientu */}
                <path
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth="12"
                    strokeLinecap="round"
                    className="opacity-10"
                />

                {/* POSTĘP - Widzimy tylko tę część gradientu, którą odsłania maska */}
                <path
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth="12"
                    strokeLinecap="round"
                    mask={`url(#${maskId})`}
                />

                {/* Wskazówka (Needle) */}
                <g
                    style={{
                        transform: `rotate(${animatedPercent * 180}deg)`,
                        transformOrigin: '100px 100px',
                        transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <polygon
                        points="100,96 100,104 26,100"
                        fill="#94a3b8"
                    />
                    <circle cx="100" cy="100" r="4.5" fill="#94a3b8" />
                </g>
            </svg>

            <div style={{
                position: 'absolute',
                top: '35%',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
                width: '100%',
            }}>
                <span style={{ color: '#f8f9fa', fontWeight: 'bold', fontSize: '1.75rem', lineHeight: 1 }}>
                    {value}
                </span>
                {secondaryInfo && (
                    <span style={{ color: '#cbd5e1', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {secondaryInfo}
                    </span>
                )}
                <span style={{ color: '#f8f9fa', fontSize: '0.75rem', marginTop: secondaryInfo ? '0.25rem' : '0.5rem', fontWeight: 600 }}>
                    {label}
                </span>
            </div>
        </div>
    );
}